import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Box,
  Typography,
  Button,
  useTheme,
  styled,
} from "@mui/material";
import {
  ChevronLeft,
  ChevronRight,
  CalendarMonth,
} from "@mui/icons-material";
import {
  startOfWeek,
  endOfWeek,
  startOfMonth,
  endOfMonth,
  eachDayOfInterval,
  isSameMonth,
  isSameDay,
  addMonths,
  format,
  isSameWeek,
} from "date-fns";
import { isWeekday } from "../utils/commonMethods";
import { useTranslation } from "react-i18next";
import { enUS, nl } from 'date-fns/locale';

interface WeekSelectCalendarProps {
  selectedDate: Date;
  onWeekSelect: (start: Date, end: Date) => void;
}

const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '10px',
    maxWidth: '360px',
    margin: '16px',
  },
}));

const CalendarButton = styled(Button)(({ theme }) => ({
  minWidth: '45px',
  height: '45px',
  padding: 0,
  borderRadius: '50%',
  fontSize: '14px',
  fontWeight: 400,
  '&.selected': {
    backgroundColor: theme.palette.calenderColors.main,
    color: '#fff',
    '&:hover': {
      backgroundColor: theme.palette.calenderColors.light,
    },
  },
}));

const ActionButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  padding: '8px 16px',
  fontWeight: 500,
  minWidth: '64px',
}));

const NavigationBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: '4px',
  marginLeft: 'auto',
}));

const WeekSelectCalendar: React.FC<WeekSelectCalendarProps> = ({
  selectedDate,
  onWeekSelect,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentMonth, setCurrentMonth] = useState(selectedDate);
  const [tempSelectedDate, setTempSelectedDate] = useState(selectedDate);
  const theme = useTheme();
  const { i18n, t } = useTranslation();

  const locale = i18n.language === 'nl' ? nl : enUS;

  const handleOpen = () => setIsOpen(true);
  const handleClose = () => {
    setIsOpen(false);
    setTempSelectedDate(selectedDate);
  };

  const handlePrevMonth = () => setCurrentMonth(addMonths(currentMonth, -1));
  const handleNextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));

  const handleDayClick = (day: Date) => {
    if (isWeekday(day)) {
      setTempSelectedDate(day);
    }
  };

  const handleOk = () => {
    const weekStart = startOfWeek(tempSelectedDate, { weekStartsOn: 1 });
    const weekEnd = endOfWeek(tempSelectedDate, { weekStartsOn: 1 });
    onWeekSelect(weekStart, weekEnd);
    setIsOpen(false);
  };

  useEffect(() => {
    setTempSelectedDate(selectedDate);
    setCurrentMonth(selectedDate);
  }, [selectedDate]);

  const renderWeeks = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(currentMonth);
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 });
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 });
    const dateRange = eachDayOfInterval({ start: startDate, end: endDate });

    const weeks = [];
    for (let i = 0; i < dateRange.length; i += 7) {
      weeks.push(dateRange.slice(i, i + 7));
    }

    return weeks.map((week, weekIndex) => (
      <Box key={weekIndex} display="flex" justifyContent="space-between">
        {week.map((day) => {
          const isCurrentMonth = isSameMonth(day, currentMonth);
          const isSelected = isSameWeek(day, tempSelectedDate, { weekStartsOn: 1 }) && isWeekday(day);
          const dayNumber = format(day, "d");

          return (
            <CalendarButton
              key={format(day, "yyyy-MM-dd")}
              onClick={() => handleDayClick(day)}
              className={isSelected ? 'selected' : ''}
              disabled={!isCurrentMonth}
              sx={{
                color: !isCurrentMonth ? '#bdbdbd' : 'inherit',
              }}
            >
              {dayNumber}
            </CalendarButton>
          );
        })}
      </Box>
    ));
  };

  return (
    <>
      <IconButton
        size="small"
        onClick={handleOpen}
        sx={{
          bgcolor: theme.palette.blueColors.main,
          borderRadius: "8px",
          "&:hover": {
            bgcolor: theme.palette.blueColors.main,
            backgroundColor: theme.palette.blueColors.main,
          },
        }}
      >
        <CalendarMonth fontSize="medium" sx={{ color: theme.palette.secondary.main }} />
      </IconButton>

      <StyledDialog open={isOpen} onClose={handleClose}
      >
        <DialogTitle sx={{ px: 2, py: 2 }}>
          <Box display="flex" alignItems="center" justifyContent="space-between">
            {/* <NavigationBox> */}
            <Typography
              sx={{
                fontSize: '16px',
                fontWeight: 500,
                color: 'rgba(0, 0, 0, 0.87)',
              }}
            >
              {format(currentMonth, "MMMM yyyy", { locale })}
            </Typography>
            <Box>
              <IconButton onClick={handlePrevMonth} size="small" sx={{ padding: '4px' }}>
                <ChevronLeft sx={{ fontSize: 20 }} />
              </IconButton>
              <IconButton onClick={handleNextMonth} size="small" sx={{ padding: '4px' }}>
                <ChevronRight sx={{ fontSize: 20 }} />
              </IconButton>
            </Box>
            {/* </NavigationBox> */}
          </Box>
        </DialogTitle>

        <DialogContent sx={{ px: 2, py: 1 }}>
          <Box display="flex" flexDirection="column">
            <Box
              display="flex"
              justifyContent="space-between"
              mb={1}
            >
              {["Mo", "Tu", "We", "Th", "Fr", "Sa", "Su"].map((day) => (
                <Typography
                  key={day}
                  sx={{
                    width: '50px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: 500,
                    color: 'rgba(0, 0, 0, 0.6)',
                  }}
                >
                  {t(`daysTwoDigit.${day}`)}
                </Typography>
              ))}
            </Box>
            {renderWeeks()}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 2, py: 1 }}>
          <ActionButton
            onClick={handleClose}
            sx={{ color: theme.palette.blueColors.main }}
          >
            {t("common.label_cancel")}
          </ActionButton>
          <ActionButton
            onClick={handleOk}
            sx={{ color: theme.palette.blueColors.main }}
          >
            {t("common.label_ok")}
          </ActionButton>
        </DialogActions>
      </StyledDialog>
    </>
  );
};

export default WeekSelectCalendar;